import { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { NotificationToastContainer } from '../notifications/NotificationToast';
import { useNotificationStore } from '../../store/notificationStore';
import { SidebarProvider } from '../../contexts/SidebarContext';
import { GlobalSidebars } from '../crm/GlobalSidebars';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { GlobalSearchModal } from '../search/GlobalSearchModal';
import { useActiveSection } from '../../hooks/useActiveSection';
import { ENCRE_SECTIONS, SECTIONS } from '../../config/navigation';
import { HeaderBar } from './HeaderBar';
import { ContextualSidebar } from './ContextualSidebar';
import { MobileNavDrawer } from './MobileNavDrawer';
import { ChunkErrorBoundary } from '../ChunkErrorBoundary';
import { ShellContext } from './shellContext';
import { WorkshopScreenHost } from './WorkshopScreenDock';
import { ReturnToBureau } from './ReturnToBureau';
import { useBadge } from '../../hooks/useBadge';
import { useDeferredAppUpdate } from '../../hooks/useDeferredAppUpdate';
import { useWindowControlsOverlay } from '../../hooks/useWindowControlsOverlay';
import { InstallPrompt } from '../pwa/InstallPrompt';
import { OfflineBanner } from '../pwa/OfflineBanner';
import { Softphone } from '../telephony/Softphone';
import { useCanUseTelephony } from '../../lib/telephonyAccess';
import { ScreenGate } from '../auth/ScreenGate';

/**
 * The admin's frame. The Otto chat panel on the right is gone (Gilles, 12/10/2026: « tout passe par
 * le Bureau, plus besoin du chat Otto à droite »): asking Otto is giving him a mission (`useAskOtto`).
 */
export default function AdminLayout() {
  return (
    <SidebarProvider>
      <AdminLayoutInner />
    </SidebarProvider>
  );
}

function AdminLayoutInner() {
  const globalSearch = useGlobalSearch();
  const canTelephony = useCanUseTelephony();
  const { activeToasts, removeToast, markAsRead } = useNotificationStore();
  const activeSection = useActiveSection();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // PWA : badge dock + détection Window Controls Overlay (effets globaux)
  useBadge();
  useWindowControlsOverlay();
  // Nouvelle version déployée : appliquée au prochain changement de page, pas en pleine lecture.
  useDeferredAppUpdate();
  const sectionConfig = SECTIONS.find(s => s.id === activeSection);
  // The Bureau and the Équipe draw their own chrome, Encre's sidebar (PLAN-BUREAU § 4 quater): no
  // header, no section sidebar, no drawer here; the tools keep theirs until their own redesign.
  const ownChrome = ENCRE_SECTIONS.includes(activeSection);
  const shell = useMemo(() => ({ openSearch: globalSearch.openSearch }), [globalSearch.openSearch]);
  const hasLeftSidebar = !ownChrome && activeSection !== 'dashboard' && activeSection !== 'settings';
  const sidebarWidth = sectionConfig?.sidebarWidth || 'w-52';
  const isFullBleed = sectionConfig?.fullBleedContent;

  // Keep global keyboard shortcut for quick note (Ctrl+Shift+N)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'N') {
        e.preventDefault();
        window.location.href = '/admin/notes?new=1';
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ShellContext.Provider value={shell}>
    <div className={`h-screen w-screen flex overflow-hidden ${ownChrome ? 'bg-desk-bg' : 'bg-neutral-50'}`}>
      {/* Left sidebar column — desktop only (≥ lg). Sous lg → MobileNavDrawer. */}
      <div
        className={`hidden lg:block flex-shrink-0 transition-all duration-300 overflow-hidden ${
          hasLeftSidebar ? sidebarWidth : 'w-0'
        }`}
      >
        <div className={`${sidebarWidth} h-full`}>
          <ContextualSidebar />
        </div>
      </div>

      {/* Drawer de navigation mobile (< lg) */}
      {!ownChrome && <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />}

      {/* Center column — header + scrollable content */}
      <div className="flex-1 min-w-0 flex flex-col relative">
        {/* No Otto chat here any more (12/10/2026): asking Otto is a mission, in the Bureau. */}
        {!ownChrome && (
          <HeaderBar
            onOpenSearch={globalSearch.openSearch}
            onOpenMobileNav={() => setMobileNavOpen(true)}
          />
        )}
        {/* A tool's page opened from the Bureau (« Ouvrir dans le CRM »): the way back. */}
        {!ownChrome && <ReturnToBureau />}

        <main className={`min-h-0 flex-1 ${isFullBleed ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div className={isFullBleed ? 'h-full' : 'py-4 px-4 sm:py-6 sm:px-6 lg:px-8'}>
            <ChunkErrorBoundary>
              <Suspense fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#0066FF]"></div>
                </div>
              }>
                <ScreenGate>
                  <Outlet />
                </ScreenGate>
              </Suspense>
            </ChunkErrorBoundary>
          </div>
        </main>

        {/* CRM detail sidebars — positioned within center column */}
        <GlobalSidebars />
      </div>

      {/* Overlays — unchanged */}
      {/* Overlays */}
      <NotificationToastContainer
        toasts={activeToasts}
        onClose={removeToast}
        onMarkAsRead={markAsRead}
      />
      <GlobalSearchModal
        isOpen={globalSearch.isSearchOpen}
        onClose={globalSearch.closeSearch}
        query={globalSearch.query}
        setQuery={globalSearch.setQuery}
        results={globalSearch.results}
        isLoading={globalSearch.isLoading}
        totalCount={globalSearch.totalCount}
        activeIndex={globalSearch.activeIndex}
        setActiveIndex={globalSearch.setActiveIndex}
        flatResults={globalSearch.flatResults}
        handleKeyDown={globalSearch.handleKeyDown}
        handleResultSelect={globalSearch.handleResultSelect}
      />
      {/* Softphone global — réservé au profil propriétaire (téléphonie single-seat) */}
      {canTelephony && <Softphone />}
      {/* The workshop's screen: one window for every page, so it stays open from page to page. */}
      <WorkshopScreenHost />
      <InstallPrompt />
      <OfflineBanner />
    </div>
    </ShellContext.Provider>
  );
}
